import {ref} from 'vue'
import {useI18n} from 'vue-i18n'
import YarismaService from '@/services/yarisma'
import type {TimerService} from '@/services/timer'
import type {IYarismaKalansure} from '@/model/yarisma'
import {ErrorPopup} from '@/utils/popup'
import type {ApiError} from '@/services/api'

export function useKalanSureler() {
  const { t } = useI18n()
  const baslamasinaKalanSure = ref(0)
  const bitmesineKalanSure = ref(0)
  const yarismaKalansure = ref<IYarismaKalansure | null>({
    baslangic_tarihi: '',
    baslangic_saati: '',
    baslamasina_kalan_sure: 0,
    bitmesine_kalan_sure: 0,
    yarismaci_bitirmis_mi: false,
  })

  const baslamasinaKalanSureTimer = ref<TimerService | undefined>()
  const bitmesineKalanSureTimer = ref<TimerService | undefined>()
  const sorularPreloadTimer = ref<TimerService | undefined>()

  let sunucuKalanSureTimeoutID: number | undefined

  /**
   * Sunucudan kalan süreleri getirir ve state'leri günceller
   */
  async function getSunucuKalanSure(localFirst: boolean = false): Promise<[ApiError, boolean, IYarismaKalansure]> {
    const [err, bittiMi, kalansure] = await YarismaService.getYarismaKalansure(localFirst)
    if (bittiMi) {
      kalanSurelerCleanup()
      return [null, true, kalansure]
    }
    if (err) {
      ErrorPopup(t('error_page.error_refresh_page') + " " + t('error_page.error_detail') + ": " + err)
      return [err, false, kalansure]
    }
    yarismaKalansure.value = kalansure
    baslamasinaKalanSure.value = kalansure.baslamasina_kalan_sure
    bitmesineKalanSure.value = kalansure.bitmesine_kalan_sure

    return [null, false, kalansure]
  }

  /**
   * Başlamasına kalan süre geri sayımını başlatır
   * Süre bitince yarisma sayfasına yönlendirir
   */
  function baslamasinaGeriSayimBaslat() {
    baslamasinaKalanSureTimer.value = YarismaService.baslamasinaKalanSureGeriSayimiBaslat(
      baslamasinaKalanSure,
      sorularPreloadTimer
    )
    bitmesineGeriSayimBaslat()
  }

  /**
   * Bitmesine kalan süre geri sayımını başlatır
   * Sadece bitmesine kalan süreyi azaltır ve süre bitince anasayfaya yönlendirir
   */
  function bitmesineGeriSayimBaslat() {
    if (bitmesineKalanSureTimer.value) {
      return;
    }
    bitmesineKalanSureTimer.value = YarismaService.bitmesineKalanSureGeriSayimiBaslat(
      bitmesineKalanSure
    )
  }

  function baslamasinaGeriSayiminiDurdur() {
    baslamasinaKalanSureTimer.value?.stop()
    sorularPreloadTimer.value?.stop()
    sunucuKalanSureTimerDurdur()
  }

  /**
   * Sunucudan periyodik olarak kalan süreyi getiren timer'ı başlatır
   * @param intervalMs - İnterval süresi (ms), varsayılan 10 saniye
   */
  function sunucuKalanSureTimerBaslat(dontStop: boolean = false) {
    // Eğer zaten bir timer varsa, önce durdur
    if (!dontStop) {
      sunucuKalanSureTimerDurdur()
    }

    // Periyodik çağrıları başlat
    const kalanSureInterval = () => {
      sunucuKalanSureTimeoutID = setTimeout(async () => {
        const [err, bittiMi] = await getSunucuKalanSure()
        if (err || bittiMi) {
          return
        }
        kalanSureInterval()
      }, getNextTimeoutInterval())
    }
    kalanSureInterval()
  }

  function getNextTimeoutInterval(): number {
    const SECOND = 1000
    const MINUTE = 60

    let baseInterval = 0

    // Yarışma henüz başlamadıysa (bekleme odasında)
    if (baslamasinaKalanSure.value > 60 * MINUTE) {       // > 1 saat
      baseInterval = 90 * SECOND
    } else if (baslamasinaKalanSure.value > 30 * MINUTE) { // 30dk - 1 saat
      baseInterval = 60 * SECOND
    } else if (baslamasinaKalanSure.value > 10 * MINUTE) { // 10-30 dk
      baseInterval = 30 * SECOND
    } else if (baslamasinaKalanSure.value > 2 * MINUTE) {  // 2-10 dk
      baseInterval = 15 * SECOND
    } else if (baslamasinaKalanSure.value > 0) {  // < 2 dk - başlangıç kritik
      baseInterval = 10 * SECOND
    }
    // Yarışma aktif
    else if (bitmesineKalanSure.value > 30 * MINUTE) {    // > 30 dk kaldı
      baseInterval = 90 * SECOND
    } else if (bitmesineKalanSure.value > 10 * MINUTE) {  // 10-30 dk kaldı
      baseInterval = 60 * SECOND
    } else if (bitmesineKalanSure.value > 5 * MINUTE) {   // 5-10 dk kaldı
      baseInterval = 30 * SECOND
    } else if (bitmesineKalanSure.value > 1 * MINUTE) {   // 1-5 dk kaldı
      baseInterval = 15 * SECOND
    } else {                                               // < 1 dk - bitiş kritik
      baseInterval = 30 * SECOND // fallback
    }

    // Jitter ekle (±25%) - aynı anda istek atmayı önle
    return addJitter(baseInterval, 0.25)
  }

  function addJitter(baseMs: number, jitterPercent: number): number {
    const jitter = baseMs * jitterPercent
    return Math.floor(baseMs - jitter + Math.random() * jitter * 2)
  }
  /**
   * Sunucu kalan süre timer'ını durdurur
   */
  function sunucuKalanSureTimerDurdur() {
    clearTimeout(sunucuKalanSureTimeoutID)
    sunucuKalanSureTimeoutID = undefined
  }

  /**
   * Tüm timer'ları temizler
   */
  function kalanSurelerCleanup() {
    baslamasinaKalanSureTimer.value?.stop()
    baslamasinaKalanSureTimer.value = undefined
    bitmesineKalanSureTimer.value?.stop()
    bitmesineKalanSureTimer.value = undefined
    sorularPreloadTimer.value?.stop()
    sorularPreloadTimer.value = undefined
    sunucuKalanSureTimerDurdur()
  }

  // Component unmount olduğunda timer'ları temizle
  //onUnmounted(kalanSurelerCleanup)

  return {
    // State
    baslamasinaKalanSure,
    bitmesineKalanSure,
    yarismaKalansure,
    baslamasinaKalanSureTimer,
    bitmesineKalanSureTimer,

    // Actions
    getSunucuKalanSure,
    sunucuKalanSureTimerBaslat,
    sunucuKalanSureTimerDurdur,
    baslamasinaGeriSayimBaslat,
    bitmesineGeriSayimBaslat,
    baslamasinaGeriSayiminiDurdur,
    kalanSurelerCleanup
  }
}

